import type { ReactNode } from 'react';
import { MEMBERSHIP_LEVEL_DESCRIPTIONS, type WriteAxis } from '@ni/shared';
import { useDemo, useMayWrite, useOrg } from '../api/app-context';

/**
 * **Affordances this reader could not use are not drawn.**
 *
 * Two things cap what a reader may change and they compose as a minimum: a demo
 * organization, which caps everyone at nothing, and their own membership level.
 * `useMayWrite` folds both, so this component asks one question.
 *
 * `needs` is what the control behind it would write — `all` for research, an
 * import or a merge, `notes` for the add box and the interaction log. It
 * defaults to `all` because that is what almost every control here is, and
 * because a default that under-restricts is the wrong way round.
 *
 * **It may be stricter than the server's axis for that write, never looser.**
 * A control drawn for a member the server will refuse is a control that does
 * not work; a control withheld from a member the server would allow is only a
 * missing affordance. When the write behind the control is a named `api`
 * method, take `needs` from `axisOfWrite` rather than writing the axis here,
 * so the server tightening the route tightens the control with it
 * (write-axis contract note, §80).
 *
 * **Server policy remains authoritative.** `WriteAccessGuard` refuses the write
 * whatever this renders; what this buys is a page a standard member can read
 * without meeting a refusal on every second control.
 */
export function IfWrites({ needs = 'all', children }: { needs?: WriteAxis; children: ReactNode }) {
  return useMayWrite(needs) ? <>{children}</> : null;
}

/**
 * The standing line at the top of the app saying what this reader may do, for
 * the readers who may not do everything.
 *
 * Demo wins when both apply: "fixed fictional data" is the more absolute thing
 * to know about a page, and it is the same order the server reports its two
 * refusals in.
 */
export function AccessNotice() {
  const demo = useDemo();
  const level = useOrg().level;
  const everything = useMayWrite('all');
  if (demo) {
    return (
      <aside className="demo-notice" role="note">
        <strong>Read-only demo</strong>
      </aside>
    );
  }
  if (everything) return null;
  return (
    <aside className="demo-notice" role="note">
      <strong>{MEMBERSHIP_LEVEL_DESCRIPTIONS[level]}</strong>
    </aside>
  );
}
